import { Button, IconButton, Text, VSpacer } from '@/components/DesignSystem';
import './DatePickerStyles.css';
import {
  DropdownYearPicker,
  FIRST_YEAR,
  LAST_YEAR,
} from '@/components/DesignSystem/DatePicker/DropdownYearPicker';
import { SXStyles } from '@/themes/variant-interfaces/SXStyles';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import { Box, Divider, Stack } from '@mui/material';
import TouchRipple from '@mui/material/ButtonBase/TouchRipple';
import { DateTime } from 'luxon';
import { Fragment, MouseEvent, TouchEvent, useEffect, useMemo, useRef, useState } from 'react';
import { DropdownMonthPicker } from './DropdownMonthPicker';
import { useTranslation } from 'react-i18next';

const MAX_WEEK_ROWS = 6;
// Matches the weekday row plus six weeks, so switching to the month or year grid keeps the
// popover's height and it stays where it was positioned.
const DROPDOWN_BODY_HEIGHT = 372;

type BoundsUnit = 'month' | 'year';

interface StepButtonProps {
  ariaLabel: string,
  isDisabled: boolean,
  isHidden: boolean,
  isNext: boolean,
  onClick: () => void,
  testID: string,
}

// A hidden arrow keeps its space so the label stays put while the other list is open.
const StepButton = ({
  ariaLabel,
  isDisabled,
  isHidden,
  isNext,
  onClick,
  testID,
}: StepButtonProps) => (
  <Box style={isHidden ? { visibility: 'hidden' } : undefined}>
    <IconButton
      aria-label={ariaLabel}
      disabled={isDisabled || isHidden}
      onClick={onClick}
      size="small"
      testID={testID}
    >
      {isNext ? <NavigateNext /> : <NavigateBefore />}
    </IconButton>
  </Box>
);

interface DropdownHeaderGroupProps {
  isListOpen: boolean,
  isOtherListOpen: boolean,
  isSelectable: (month: DateTime) => boolean,
  label: string,
  listLabel: string,
  listTestID: string,
  next: DateTime,
  nextLabel: string,
  onStep: (month: DateTime) => void,
  onToggleList: () => void,
  previous: DateTime,
  previousLabel: string,
  stepTestID: string,
}

const DropdownHeaderGroup = ({
  isListOpen,
  isOtherListOpen,
  isSelectable,
  label,
  listLabel,
  listTestID,
  next,
  nextLabel,
  onStep,
  onToggleList,
  previous,
  previousLabel,
  stepTestID,
}: DropdownHeaderGroupProps) => (
  <Stack alignItems="center" direction="row">
    <StepButton
      ariaLabel={previousLabel}
      isDisabled={!isSelectable(previous)}
      isHidden={isOtherListOpen}
      isNext={false}
      onClick={() => onStep(previous)}
      testID={`${stepTestID}-prev`}
    />
    <Button
      aria-expanded={isListOpen}
      aria-label={listLabel}
      disabled={isOtherListOpen}
      endIcon={isListOpen ? <ArrowDropUp /> : <ArrowDropDown />}
      onClick={onToggleList}
      size="small"
      sx={{ color: 'text.primary' }}
      testID={listTestID}
      variant="text"
    >
      <Text category="label-large">{label}</Text>
    </Button>
    <StepButton
      ariaLabel={nextLabel}
      isDisabled={!isSelectable(next)}
      isHidden={isOtherListOpen}
      isNext
      onClick={() => onStep(next)}
      testID={`${stepTestID}-next`}
    />
  </Stack>
);

const styles: SXStyles = {
  mainContainer: {
    padding: "8px",
    minWidth: 340,
  },
  dateRangeContainer: {
    maxHeight: 456,
    overflowY: "scroll",
  },
  yearPickerContainer: {
    maxHeight: 280,
    overflowY: "scroll",
  },
} as const;

export interface DatePickerCalendarProps {
  internalEndDate?: Date,
  internalStartDate?: Date,
  endDate?: Date,
  maxDate?: string,
  minDate?: string,
  onChange: (startDate: Date, endDate?: Date) => void,
  onShowMonthPicker?: (show: boolean) => void,
  onShowYearPicker?: (show: boolean) => void,
  startDate?: Date,
  testID: string,
  variant: 'modal' | 'dropdown' | 'modal-range',
}

interface CalendarItemProps {
  highlight?: 'middle' | 'start' | 'end',
  isDisabled?: boolean,
  isSelected: boolean,
  isToday: boolean,
  onChange: (value: DateTime) => void,
  testID: string,
  value?: DateTime,
}

interface YearPickerItemProps {
  isSelected: boolean,
  onChange: (value: number) => void,
  value: number,
}

interface CalendarProps {
  endDate?: DateTime,
  maxDate?: string,
  minDate?: string,
  monthAndYear: DateTime,
  onChange: (startDate: DateTime, endDate?: DateTime) => void,
  showDateRange?: boolean,
  startDate?: DateTime,
  testID: string,
  // Pads short months with empty weeks so the calendar keeps one height.
  weekRows?: number,
}

interface CalendarRangeProps {
  luxonEndDate?: DateTime,
  luxonStartDate?: DateTime,
  months: DateTime[],
  onChange: (startDate: DateTime, endDate?: DateTime) => void,
  testID: string,
}

const Calendar = ({
  endDate,
  maxDate,
  minDate,
  monthAndYear,
  onChange,
  showDateRange = false,
  startDate,
  testID,
  weekRows,
}: CalendarProps) => {

  const today = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const handleOnChange = (selectedDate: DateTime) => {

    if (!showDateRange) {
      onChange(selectedDate);
      return;
    }

    if (!startDate) {
      onChange(selectedDate, undefined);
    } else if (!endDate) {
      if (selectedDate > startDate) {
        onChange(startDate, selectedDate);
      } else {
        onChange(selectedDate, undefined);
      }
    } else {
      onChange(selectedDate, undefined);
    }
  };

  const daysArray: (DateTime | undefined)[][] = useMemo(() => {
    let currentDay = DateTime.fromISO(monthAndYear.toFormat("yyyy-MM-01"));
    const days: (DateTime | undefined)[][] = [];

    while (currentDay.month === monthAndYear.month) {
      const row: (DateTime | undefined)[] = [];
      for (let weekday = 0; weekday < 7; weekday += 1) {
        const luxonWeekday = weekday === 0 ? 7 : weekday;
        if (currentDay.day === 1) {
          if (currentDay.weekday === luxonWeekday) {
            row.push(currentDay);
            currentDay = currentDay.plus({ days: 1 });
          } else {
            row.push(undefined);
          }
        } else if (currentDay.month === monthAndYear.month) {
          row.push(currentDay);
          currentDay = currentDay.plus({ days: 1 });
          if (currentDay.day === 1) {
            break;
          }
        }
      }
      days.push(row);
    }

    const lastRow = days[days.length - 1];
    for (let i = lastRow.length; i < days[days.length - 2].length; i += 1) {
      lastRow.push(undefined);
    }

    while (weekRows && days.length < weekRows) {
      days.push(Array(7).fill(undefined));
    }

    return days;
  }, [monthAndYear, weekRows]);

  const getHighlightType = (
    row: (DateTime | undefined)[],
    startSelected: boolean,
    endSelected: boolean,
    itemDate?: DateTime,
  ) => {
    if (!showDateRange) {
      return undefined;
    }

    const datesAreEqual = !!startDate && !!endDate && startDate.equals(endDate);

    if (!itemDate && !!startDate && !!endDate && startDate.month !== endDate.month) {
      const dates = [...row.filter(x => !!x)];
      const firstOrLastCell = (
        row[0]
          ? dates.pop()
          : dates.shift()
      ) as DateTime;

      if (
        (firstOrLastCell < endDate && firstOrLastCell > startDate) ||
        (!row[0] && firstOrLastCell.equals(endDate)) ||
        (row[0] && firstOrLastCell.equals(startDate))
      ) {
        return 'middle';
      }
    }

    if (startSelected && !datesAreEqual && !!endDate) {
      return 'start';
    }
    else if (endSelected && !datesAreEqual) {
      return 'end';
    }
    else if (
      itemDate && startDate && endDate
      && itemDate > startDate && itemDate < endDate
    ) {
      return 'middle';
    }
  };

  const renderItem =
  (
    row: (DateTime | undefined)[],
    colIndex: number,
    itemDate?: DateTime,
  ) => {

    const startSelected = itemDate
      ? startDate?.equals(itemDate)
      : false;
    const endSelected = itemDate
      ? endDate?.equals(itemDate)
      : false;

    const highlightType = getHighlightType(
      row,
      startSelected || false,
      endSelected || false,
      itemDate,
    );
    const isoDate = itemDate?.toISODate();
    const isOutOfRange = !!isoDate
      && ((!!minDate && isoDate < minDate) || (!!maxDate && isoDate > maxDate));
    return (
      <CalendarItem
        highlight={highlightType}
        isDisabled={isOutOfRange}
        isSelected={!!startSelected || !!endSelected}
        isToday={itemDate ? today.equals(itemDate) : false}
        key={`${itemDate?.toISO()}-${colIndex}`}
        onChange={handleOnChange}
        testID={testID}
        value={itemDate}
      />
    );
  };

  return (
    <Box sx={styles.mainContainer}>
      <table
        className="calendar-table-container"
        id={`calendar-month-picker-${monthAndYear.toFormat('M-yyyy')}`}
      >
        <tbody>
          {daysArray.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {
                row.map((col, colIndex) => {
                  return renderItem(
                    row,
                    colIndex,
                    col,
                  );
                })
              }
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

const CalendarItem = ({
  highlight,
  isDisabled = false,
  isSelected,
  isToday,
  onChange,
  testID,
  value,
}: CalendarItemProps) => {
  const rippleRef = useRef<{
    start: (event: MouseEvent | TouchEvent) => void,
    stop: (event: MouseEvent | TouchEvent) => void,
  }>(null);
  const onRippleStart = (e: MouseEvent | TouchEvent) => {
    rippleRef?.current?.start(e);
  };
  const onRippleStop = (e: MouseEvent | TouchEvent) => {
    rippleRef?.current?.stop(e);
  };

  const getClassName = () => {
    if (isDisabled) {
      return "calendar-day-disabled";
    }
    if (isSelected) {
      return "calendar-day-selected";
    } else if (isToday) {
      return "calendar-day-today";
    }
    return "";
  };

  return (
    <td
      className="calendar-day-container"
    >
      <div className={highlight ? `calendar-day-range-highlight-${highlight}` : ''}>
      </div>
      {value && (
        <div
          aria-disabled={isDisabled}
          className={['calendar-day', getClassName()].join(' ')}
          data-testid={`${testID}-day-${value.toFormat('M-d')}`}
          onClick={() => {
            if (!isDisabled) {
              onChange(value);
            }
          }}
          onMouseDown={onRippleStart}
          onMouseUp={onRippleStop}
          onTouchEnd={onRippleStop}
          onTouchStart={onRippleStart}
        >
          <Text category="body-large">
            {value.toFormat('d')}
          </Text>
          <TouchRipple center ref={rippleRef} />
        </div>
      )}
    </td>
  );
};

const YearPickerItem = ({
  isSelected,
  onChange,
  value,
}: YearPickerItemProps) => {
  const rippleRef = useRef<{
    start: (event: MouseEvent | TouchEvent) => void,
    stop: (event: MouseEvent | TouchEvent) => void,
  }>(null);
  const onRippleStart = (e: MouseEvent | TouchEvent) => {
    rippleRef?.current?.start(e);
  };
  const onRippleStop = (e: MouseEvent | TouchEvent) => {
    rippleRef?.current?.stop(e);
  };

  return (
    <td className='calendar-year-item-container'>
      <div
        className={[
          'calendar-year-item',
          isSelected ? 'calendar-year-item-selected' : '',
        ].join(' ')}
        onClick={() => onChange(value)}
        onMouseDown={onRippleStart}
        onMouseUp={onRippleStop}
        onTouchEnd={onRippleStop}
        onTouchStart={onRippleStart}
      >
        <Text
          category="body-large"
          id={`calendar-year-picker-${value}`}
        >
          {value}
        </Text>
      </div>
    </td>
  );
};

export const CalendarRange = ({
  luxonEndDate,
  luxonStartDate,
  months,
  onChange,
  testID,
}: CalendarRangeProps) => {
  const { i18n } = useTranslation();
  return (
    <Box
      className="scrollable-container"
      sx={styles.dateRangeContainer}
    >
    {months.map((month) => (
      <Fragment key={month.toFormat('LLLL yyyy')}>
        <Box px={4.5} py={1.8}>
          <Text category="title-small">
            {month.setLocale(i18n.language).toFormat('LLLL yyyy')}
          </Text>
        </Box>
        <Calendar
          endDate={luxonEndDate}
          monthAndYear={month}
          onChange={onChange}
          showDateRange
          startDate={luxonStartDate}
          testID={testID}
        />
      </Fragment>
    ))}
    </Box>
  );
};

export const DatePickerCalendar = ({
  internalEndDate,
  internalStartDate,
  maxDate,
  minDate,
  onChange,
  onShowMonthPicker,
  onShowYearPicker,
  testID,
  variant,
}: DatePickerCalendarProps) => {

  const calendarContainerRef = useRef<HTMLElement>(null);
  const { i18n, t } = useTranslation();
  const locale = i18n.language;

  // Localized narrow day names: Sun–Sat (ISO weekday: Sun=7, Mon=1…Sat=6)
  const localizedDayNames = useMemo(
    () => Array.from({ length: 7 }, (_, i) =>
      DateTime.now().set({ weekday: i === 0 ? 7 : i }).setLocale(locale).toFormat('EEEEE'),
    ),
    [locale],
  );

  const [monthAndYear, setMonthAndYear] = useState(
    internalStartDate ? DateTime.fromJSDate(internalStartDate) : DateTime.now(),
  );
  const [luxonStartDate, setLuxonStartDate] = useState<DateTime | undefined>();
  const [luxonEndDate, setLuxonEndDate] = useState<DateTime | undefined>();
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  useEffect(() => {
    if (internalStartDate) {
      document.getElementById(
        `calendar-month-picker-${internalStartDate.getMonth()+1}-${internalStartDate.getFullYear()}`,
      )?.scrollIntoView?.();
    }
  }, [internalStartDate]);

  useEffect(() => {
    onShowMonthPicker?.(showMonthPicker);
  }, [showMonthPicker, onShowMonthPicker]);

  useEffect(() => {
    onShowYearPicker?.(showYearPicker);
  }, [showYearPicker, onShowYearPicker]);

  useEffect(() => {
    if (internalStartDate) {
      // Create Luxon DateTime object directly from the JS Date object
      // This ensures the correct date is used regardless of timezone
      const dt = DateTime.fromJSDate(internalStartDate).startOf('day');
      setLuxonStartDate(dt);
    } else {
      setLuxonStartDate(undefined);
    }
  }, [internalStartDate]);

  useEffect(() => {
    if (internalEndDate) {
      // Create Luxon DateTime object directly from the JS Date object
      // This ensures the correct date is used regardless of timezone
      const dt = DateTime.fromJSDate(internalEndDate).startOf('day');
      setLuxonEndDate(dt);
    } else {
      setLuxonEndDate(undefined);
    }
  }, [internalEndDate]);

  const handleMonthChange = (month: number) => {
    setMonthAndYear(monthAndYear.set({ month }));
    setShowMonthPicker(false);
  };

  // A year whose same month is out of bounds opens the nearest allowed month instead.
  const clampToBounds = (month: DateTime) => {
    if (minDate && (month.endOf('month').toISODate() ?? '') < minDate) {
      return DateTime.fromISO(minDate);
    }
    if (maxDate && (month.startOf('month').toISODate() ?? '') > maxDate) {
      return DateTime.fromISO(maxDate);
    }
    return month;
  };

  const handleYearChange = (year: number) => {
    setMonthAndYear(clampToBounds(monthAndYear.set({ year })));
    setShowYearPicker(false);
  };

  const stepTo = (month: DateTime) => {
    setMonthAndYear(clampToBounds(month));
  };

  const handleOnChange = (startDate: DateTime, endDate?: DateTime) => {
    onChange(startDate.toJSDate(), endDate?.toJSDate());
  };

  const scrollIntoView = (id: string) => {
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView?.();
    }, 0);
  };

  const toggleMonthPicker = () => {
    setShowMonthPicker(!showMonthPicker);
  };

  const toggleYearPicker = () => {
    setShowYearPicker(!showYearPicker);
    if (!showYearPicker && variant !== 'dropdown') {
      scrollIntoView(`calendar-year-picker-${Math.max(monthAndYear?.year - 3, 1900)}`);
    }
  };

  const isWithinBounds = (date: DateTime, unit: BoundsUnit) => {
    const firstDay = date.startOf(unit);
    const lastDay = date.endOf(unit);
    if (lastDay.year < FIRST_YEAR || firstDay.year > LAST_YEAR) {
      return false;
    }
    const firstIsoDay = firstDay.toISODate() ?? '';
    const lastIsoDay = lastDay.toISODate() ?? '';
    return (!maxDate || firstIsoDay <= maxDate) && (!minDate || lastIsoDay >= minDate);
  };

  const isMonthSelectable = (month: DateTime) => isWithinBounds(month, 'month');
  const isYearSelectable = (year: DateTime) => isWithinBounds(year, 'year');

  const renderDropdownHeader = () => (
    <Stack
      alignItems="center"
      direction="row"
      height={64}
      justifyContent="space-between"
      px={1.5}
    >
      <DropdownHeaderGroup
        isListOpen={showMonthPicker}
        isOtherListOpen={showYearPicker}
        isSelectable={isMonthSelectable}
        label={monthAndYear.setLocale(locale).toFormat('MMM')}
        listLabel={t($ => $.designSystem.datePicker.chooseMonth, {
          month: monthAndYear.setLocale(locale).toFormat('MMMM'),
        })}
        listTestID="date-picker-calendar-show-month-picker"
        next={monthAndYear.plus({ months: 1 })}
        nextLabel={t($ => $.designSystem.datePicker.nextMonth)}
        onStep={stepTo}
        onToggleList={toggleMonthPicker}
        previous={monthAndYear.minus({ months: 1 })}
        previousLabel={t($ => $.designSystem.datePicker.previousMonth)}
        stepTestID={`${testID}-month`}
      />
      <DropdownHeaderGroup
        isListOpen={showYearPicker}
        isOtherListOpen={showMonthPicker}
        isSelectable={isYearSelectable}
        label={monthAndYear.toFormat('yyyy')}
        listLabel={t($ => $.designSystem.datePicker.chooseYear, {
          year: monthAndYear.toFormat('yyyy'),
        })}
        listTestID={`${testID}-show-year-picker`}
        next={monthAndYear.plus({ years: 1 })}
        nextLabel={t($ => $.designSystem.datePicker.nextYear)}
        onStep={stepTo}
        onToggleList={toggleYearPicker}
        previous={monthAndYear.minus({ years: 1 })}
        previousLabel={t($ => $.designSystem.datePicker.previousYear)}
        stepTestID={`${testID}-year`}
      />
    </Stack>
  );

  const ModalHeader = () => (
    <Box px={3}>
      <Stack
        alignItems="center"
        direction="row"
        height={56}
        justifyContent="space-between"
      >
        <Stack alignItems="center" direction="row">
          <Text category="label-large">
            {monthAndYear.setLocale(locale).toFormat('MMMM yyyy')}
          </Text>
          <Button
            onClick={() => toggleYearPicker()}
            size="small"
            testID={`${testID}-show-year-picker`}
            variant="text"
          >
            {showYearPicker
              ? <ArrowDropUp fontSize="small" />
              : <ArrowDropDown fontSize="small" />
            }
          </Button>
        </Stack>
        {!showYearPicker && (
          <Stack direction="row">
            <Button
              onClick={() => setMonthAndYear(monthAndYear.plus({ months: -1 }))}
              testID={`${testID}-month-prev`}
              variant="text"
            >
              <NavigateBefore />
            </Button>
            <Button
              onClick={() => setMonthAndYear(monthAndYear.plus({ months: 1 }))}
              testID={`${testID}-month-next`}
              variant="text"
            >
              <NavigateNext />
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );

  const yearsTable = useMemo(() => {
    let currentYear = 1900;
    const years = [];

    while (currentYear < 2099) {
      const row: number[] = [];
      for (let i=0; i < 3; i+=1) {
        row.push(currentYear);
        currentYear += 1;
      }
      years.push(row);
    }

    return years;
  }, []);

  const YearPicker = () => {
    return (
      <>
        <VSpacer size="3" />
        <Box
          className="scrollable-container"
          px={3}
          sx={styles.yearPickerContainer}
        >
          <table>
            <tbody>
              {yearsTable.map((row, index) => (
                <tr key={index}>
                  {
                    row.map(col => (
                      <YearPickerItem
                        isSelected={col === monthAndYear.year}
                        key={col}
                        onChange={handleYearChange}
                        value={col}
                      />
                    ))
                  }
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <VSpacer size="3" />
        <Divider />
      </>
    );
  };

  const CalendarHeader = () => {
    return (
      <Box sx={styles.mainContainer}>
        <table className="calendar-table-container">
          <tbody>
            <tr>
              {localizedDayNames.map((day, dayIndex) => (
                <td
                  className="calendar-day-container"
                  key={`${day}-${dayIndex}`}
                >
                  <Text category="body-large">
                    {day}
                  </Text>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </Box>
    );
  };

  const rangeMonths = useMemo(() => {
    const maxMonths = 5;
    const today = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const startingMonth = internalStartDate ? DateTime.fromJSDate(internalStartDate) : today;
    const endingMonth = internalEndDate ? DateTime.fromJSDate(internalEndDate) : today;

    let mounthCounter = maxMonths;
    const months = [];
    let currentMonth = startingMonth;

    while (
      !(currentMonth.month === endingMonth.month &&
      currentMonth.year === endingMonth.year)
    ) {
      months.push(currentMonth);
      currentMonth = currentMonth.plus({ months: 1 });
      mounthCounter -= 1;

      if (mounthCounter < 0) {
        break;
      }
    }

    months.push(endingMonth);

    if (months.length >= maxMonths) {
      return months;
    }

    for (let i = 1; i <= 2; i++) {
      months.splice(0, 0, startingMonth.plus({ months: -i }));
      months.push(endingMonth.plus({ months: i }));
    }
    return months;
  }, [internalStartDate, internalEndDate]);

  return (
    <>
      {variant === "modal" && <ModalHeader />}
      {variant === "dropdown" && renderDropdownHeader()}
      {showYearPicker && variant !== "dropdown" && <YearPicker />}
      {showYearPicker && variant == "dropdown" && (
        <DropdownYearPicker
          height={DROPDOWN_BODY_HEIGHT}
          isSelectable={isYearSelectable}
          monthAndYear={monthAndYear}
          onYearChange={handleYearChange}
        />
      )}
      {showMonthPicker && (
        <DropdownMonthPicker
          height={DROPDOWN_BODY_HEIGHT}
          isSelectable={isMonthSelectable}
          monthAndYear={monthAndYear}
          onMonthChange={handleMonthChange}
        />
      )}
      {!showYearPicker && !showMonthPicker && variant !== "modal-range" && (
        <>
          <CalendarHeader />
          <Calendar
            maxDate={maxDate}
            minDate={minDate}
            monthAndYear={monthAndYear}
            onChange={(startDate, endDate) => {
              const scrollTop = calendarContainerRef.current?.scrollTop || 0;
              setTimeout(() => {
                if (calendarContainerRef.current) {
                  calendarContainerRef.current.scrollTop = scrollTop;
                }
              }, 0);
              handleOnChange(startDate, endDate);
            }}
            startDate={luxonStartDate}
            testID={testID}
            weekRows={variant === 'dropdown' ? MAX_WEEK_ROWS : undefined}
          />
        </>
      )}
      {!showYearPicker && !showMonthPicker && variant === "modal-range" && (
        <>
          <CalendarHeader />
          <CalendarRange
            luxonEndDate={luxonEndDate}
            luxonStartDate={luxonStartDate}
            months={rangeMonths}
            onChange={handleOnChange}
            testID={testID}
          />
        </>
      )}
    </>
  );
};
